import { useState } from "react";
import { chibiStills, costumeLabels, splashArt, type CostumeId } from "../../data/gallery";
import { dormPacks, spinePacks } from "../../data/spineConfig";
import { SectionShell } from "../layout/SectionShell";
import { SpineStage } from "../ChibiWalker/SpineStage";
import { AssetImage } from "./AssetImage";
import styles from "./ArtShowcase.module.css";

type Tab = "splash" | "chibi";
type AnimSet = "combat" | "dorm";

export function ArtShowcase() {
  const [tab, setTab] = useState<Tab>("splash");
  const [lightbox, setLightbox] = useState<{ src: string; title: string } | null>(null);
  const [pack, setPack] = useState<CostumeId>("default");
  const [animSet, setAnimSet] = useState<AnimSet>("combat");
  const packs = animSet === "dorm" ? dormPacks : spinePacks;

  return (
    <SectionShell id="art" kicker="Splash · Chibi" title="Visuals of Mondragón">
      <div className={styles.tabs} role="tablist">
        {(
          [
            ["splash", "Splash / CG"],
            ["chibi", "Chibi"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={styles.tab}
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "splash" ? (
        <div className={styles.row}>
          {splashArt.map((piece) => (
            <button
              key={piece.id}
              type="button"
              className={styles.card}
              onClick={() => setLightbox({ src: piece.src, title: piece.title })}
            >
              <div className={styles.frame}>
                <AssetImage
                  src={piece.src}
                  alt={piece.title}
                  fallback={<p className={styles.ph}>Drop {piece.src.replace("/assets/art/splash/", "")} into public/assets/art/splash/</p>}
                />
              </div>
              <div className={styles.meta}>
                <b>{piece.title}</b>
                <span>{costumeLabels[piece.costume]} · {piece.variant}</span>
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {tab === "chibi" ? (
        <>
          <div className={styles.controls} role="tablist" aria-label="Animation set">
            {(
              [
                ["combat", "Combat"],
                ["dorm", "Dorm"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                className={styles.tab}
                aria-selected={animSet === id}
                onClick={() => setAnimSet(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className={styles.controls}>
            {packs.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.tab}
                aria-selected={pack === item.id}
                onClick={() => setPack(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <SpineStage packId={pack} packs={packs} mode={animSet} interactive height={380} />
          <p className={styles.hint}>
            {animSet === "dorm"
              ? "Dorm clips: wait / move / sit / pick / lying. Click the stage to cycle."
              : "Combat clips: wait / move / attack / victory. Click the stage to cycle."}
          </p>
          <div className={styles.row}>
            {chibiStills.map((piece) => (
              <div key={piece.id} className={styles.card}>
                <div className={styles.frame}>
                  <AssetImage
                    src={piece.src}
                    alt={piece.title}
                    fallback={<p className={styles.ph}>Still pending: {piece.title}</p>}
                  />
                </div>
                <div className={styles.meta}>
                  <b>{piece.title}</b>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {lightbox ? (
        <div className={styles.lightbox} role="dialog" aria-modal="true">
          <button type="button" className={styles.close} onClick={() => setLightbox(null)}>
            Close
          </button>
          <AssetImage
            src={lightbox.src}
            alt={lightbox.title}
            fallback={<p className={styles.ph}>No file at {lightbox.src}</p>}
          />
        </div>
      ) : null}
    </SectionShell>
  );
}
